import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { sha256 } from 'js-sha256';

import { useLocalStorageSynchronizedState } from '../../hooks/useLocalStorageSynchronizedState';

import './NewsletterPopup.scss';

export function NewsletterPopup() {
  const [isOpen, setIsOpen] = useLocalStorageSynchronizedState({
    key: 'newsletterPopup.isOpen',
    defaultValue: true,
    ttlSeconds: 24 * 60 * 60, // 1 day
  });
  const [isFullyDeployedOnSmallScreens, setIsFullyDeployedOnSmallScreens] =
    useState(false);

  const [isLargeScreen, setIsLargeScreen] = useState<any>(
    window.matchMedia('(min-width: 1680px)').matches
  );

  useEffect(() => {
    window
      .matchMedia('(min-width: 1680px)')
      .addEventListener('change', (e) => setIsLargeScreen(e.matches));
    if (isLargeScreen) {
      setIsFullyDeployedOnSmallScreens(true);
    }
  }, []);

  if (!isOpen) {
    return null;
  }

  function handleClose() {
    if (!isOpen) {
      return;
    }

    if (!isLargeScreen && isFullyDeployedOnSmallScreens) {
      setIsFullyDeployedOnSmallScreens(false);
      return;
    }

    // On large, and small devices that aren't fully deployed, entirely close
    // the thingy!
    setIsOpen(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    // Extract input data from the native form
    const data = e.target as unknown as Record<
      'name' | 'email',
      HTMLInputElement
    >;
    const name = data.name.value;
    const email = data.email.value;

    // If we don't have GTM for some reasons :shrug:
    if (!window.dataLayer) {
      return;
    }

    // Push the subscription event, not knowing who will handle it, but that's
    // not the frontend's business anyway.
    window.dataLayer.push({
      event: 'userSubscription',
      subscription: {
        add: {
          user: {
            name: name,
            email: email,
            externalId: sha256(email),
          },
        },
      },
    });
  }

  const isFullyDeployed = isLargeScreen || isFullyDeployedOnSmallScreens;

  return (
    <>
      <div
        className={cx(
          'popup-wrapper z-50 fixed w-full bottom-0 text-left flex items-center xxl_min:max-w-popupSize xxl_min:h-full xxl_min:z-1',
          isFullyDeployed && 'open'
        )}
      >
        <div className="column mx-auto w-full">
          <div
            className={cx(
              'popup-inner mx-auto w-full relative z-1 pt-16 pr-6 pb-0 pl-36 xxl_min:pt-32 xxl_min:pr-12 xxl_min:pl-8 lg_max:pb-4 lg_max:px-4 ',
              isFullyDeployed ? 'pb-4 lg_max:pt-4' : 'lg_max:pt-16'
            )}
          >
            <div
              className="popup-close cursor-pointer w-12 h-12 absolute rounded-full flex items-center justify-center z-30"
              onClick={handleClose}
            >
              <img
                src={'/images/unrankedsmurfs-login/popup-close.svg'}
                alt="popup-close"
                className="z-10"
              />
              <img
                src={'/images/unrankedsmurfs-login/popup-close-bg.svg'}
                alt="popup-close-bg"
                className="absolute z-0"
              />
            </div>

            <div className="xxl_max:flex flex-wrap w-full">
              <div
                className={cx(
                  'about-smurfing w-1/3 xxl_min:w-full lg_max:w-full',
                  isFullyDeployed ? 'lg_max:flex' : ''
                )}
              >
                <div
                  className={cx(
                    'about-img pr-6 flex items-center xxl_max:justify-center max-w-xxs w-full xxl_min:h-auto xxl_min:w-full xxl_max:absolute',
                    isFullyDeployed
                      ? 'lg_min:h-full lg_max:pr-0 lg_max:relative'
                      : ''
                  )}
                >
                  <img
                    src={'/images/unrankedsmurfs-login/diana.png'}
                    alt="popup-img"
                    className={cx(
                      'w-32 xxl_min:w-24 lg_max:max-w-120px',
                      isFullyDeployed ? 'lg_max:pb-0' : 'lg_max:pb-8'
                    )}
                  />
                </div>
                <div
                  className={cx(
                    'about-text',
                    isFullyDeployed
                      ? 'lg_max:text-left lg_max:text-left lg_max:pt-4 lg_max:pl-4'
                      : 'lg_max:text-center'
                  )}
                >
                  <p
                    className={cx(
                      'text-almost-black text-md m-0 text-almost-black pt-4 xxl_min:pt-0 lg_max:pt-0 lg_max:pb-0',
                      isFullyDeployed
                        ? 'leading-md font-semibold w-32 '
                        : 'lg_max:w-full'
                    )}
                  >
                    Are you thinking about smurfing?
                  </p>
                  {isFullyDeployed ? (
                    <small className="block lg:text-xs leading-md mt-3">
                      Unranked Smurfs is more than just an account selling
                      website. We also produce high quality content for our
                      subscribers.
                    </small>
                  ) : null}
                </div>
              </div>
              <div className="popup-opener lg:w-1/5 px-3 text-center cursor-pointer xxl_min:d-none lg_max:w-full lg_max:text-center">
                {!isFullyDeployed && (
                  <div
                    onClick={() => {
                      setIsFullyDeployedOnSmallScreens(true);
                    }}
                  >
                    <img
                      src={'/images/unrankedsmurfs-login/popup-opener.svg'}
                      alt="popup-opener"
                    />
                  </div>
                )}
              </div>
              <div
                className={cx(
                  'signup-block w-2/5 my-4 xxl_min:w-full lg_max:my-0 lg_max:w-full lg_max:w-full',
                  isFullyDeployed
                    ? ' xxl_max:pt-3 lg_max:text-left'
                    : 'lg_max:text-center'
                )}
              >
                <p className="text-almost-black text-md leading-md mb-3 lg_max:pt-0 lg_max:pb-0">
                  Signup, get guides, and win rewards
                </p>
                {isFullyDeployed ? (
                  <ul className="list-disc pl-4 pl-1 list-none  text-xs">
                    <li className="flex items-center">
                      <span className="leading-md">
                        How to reach Diamond in LoL E-Book - Written by our
                        Challenger coach.
                      </span>
                    </li>
                    <li className="flex items-center">
                      3 Fastest ways to level up your account
                    </li>
                  </ul>
                ) : null}
              </div>
              {isFullyDeployed ? (
                <React.Fragment>
                  <form
                    onSubmit={handleSubmit}
                    className={cx('mt-4 w-full xxl_max:flex lg_max:block')}
                  >
                    <div
                      className={cx(
                        'w-1/3 mr-4 xxl_min:w-full xxl_min:mb-2 lg_max:w-full lg_max:mb-2'
                      )}
                    >
                      <label htmlFor="name">Your Name</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Name"
                        required
                        className="p-2 w-full text-sm sm:leading-5"
                      />
                    </div>
                    <div
                      className={cx(
                        'w-1/3 mr-4 xxl_min:w-full xxl_min:mb-4 lg_max:w-full lg_max:mb-4'
                      )}
                    >
                      <label htmlFor="email">Your Email</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        required
                        className="p-2 w-full text-sm sm:leading-5"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="submit"
                        className="py-4 px-8 bg-absolute-white color-almost-black border-0 outline-0 text-sm lg_max:text-sm lg_max:py-3 lg_max:px-10"
                      >
                        Sign Up{' '}
                        <i className="arrow inline-block p-1 border-r border-b border-t-0 border-l-0 rotate-45"></i>
                      </button>
                    </div>
                  </form>
                  <p className="text-almost-black lg:text-sm text-sm leading-sm mt-4 w-full 2xl:text-center text-left lg_max:pt-0 lg_max:pb-0">
                    You get regular relevant content every month + free entry to
                    our giveaways. Win free accounts and RP
                  </p>
                </React.Fragment>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
